import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ProfileHeader = ({ user, onUpdateProfile }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    username: user?.username,
    email: user?.email,
    bio: user?.bio || ''
  });

  const handleSave = () => {
    onUpdateProfile(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData({
      username: user?.username,
      email: user?.email,
      bio: user?.bio || ''
    });
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-lg border border-subtle p-6">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
        {/* Avatar Section */}
        <div className="relative">
          <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center">
            <Icon name="User" size={40} color="white" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-success rounded-full flex items-center justify-center border-2 border-card">
            <Icon name="CheckCircle" size={16} color="white" />
          </div>
        </div>

        {/* Profile Info */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="space-y-4">
              <Input
                label="Username"
                type="text"
                value={editData?.username}
                onChange={(e) => setEditData({ ...editData, username: e?.target?.value })}
                className="max-w-md"
              />
              <Input
                label="Email"
                type="email"
                value={editData?.email}
                onChange={(e) => setEditData({ ...editData, email: e?.target?.value })}
                className="max-w-md"
              />
              <Input
                label="Bio"
                type="text"
                placeholder="Tell us about yourself..."
                value={editData?.bio}
                onChange={(e) => setEditData({ ...editData, bio: e?.target?.value })}
                className="max-w-md"
              />
            </div>
          ) : (
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">{user?.username}</h1>
              <p className="text-muted-foreground mb-2">{user?.email}</p>
              {user?.bio && (
                <p className="text-foreground text-sm mb-3">{user?.bio}</p>
              )}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
                  <span className="text-muted-foreground">
                    Joined {new Date(user.joinedDate)?.toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="MapPin" size={16} color="var(--color-muted-foreground)" />
                  <span className="text-muted-foreground">{user?.location || 'Not specified'}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="default" size="sm" onClick={handleSave}>
                Save Changes
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              iconPosition="left"
              onClick={() => setIsEditing(true)}
            >
              Edit Profile
            </Button>
          )}
        </div>
      </div>
      {/* Level Progress */}
      <div className="mt-6 pt-6 border-t border-subtle">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon name="Star" size={20} color="var(--color-warning)" />
            <span className="font-semibold text-foreground">Level {user?.level}</span>
            <span className="text-muted-foreground">- {user?.levelTitle}</span>
          </div>
          <span className="text-sm text-muted-foreground">
            {user?.currentXP} / {user?.nextLevelXP} XP
          </span>
        </div>
        <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-500"
            style={{ width: `${(user?.currentXP / user?.nextLevelXP) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;